import { removeAccents } from '@lib/utils/strings';
import React from 'react';
import { DefaultTheme, useTheme } from 'styled-components';
import { TAvatarSize } from './avatar-initials.types';

export const AVATAR_SIZE_MAP: Readonly<Record<TAvatarSize, number>> = {
  Xlarge: 96,
  large: 72,
  medium: 56,
  small: 40,
  Xsmall: 32,
} as const;

export function getColorCombinations(initial: string, theme: DefaultTheme) {
  const parsed = removeAccents(initial[0])?.toLocaleLowerCase();

  switch (parsed) {
    /**========================================================================
     **                            NUMBERS
     *========================================================================**/
    case '0':
      return {
        color: theme.color.custom10['90'],
        backgroundColor: theme.color.custom10['50'],
      };
    case '1':
      return {
        color: theme.color.custom07['90'],
        backgroundColor: theme.color.custom07['50'],
      };
    case '2':
      return {
        color: theme.color.custom17['90'],
        backgroundColor: theme.color.custom17['50'],
      };
    case '3':
      return {
        color: theme.color.custom18['90'],
        backgroundColor: theme.color.custom18['50'],
      };
    case '4':
      return {
        color: theme.color.custom03['90'],
        backgroundColor: theme.color.custom03['50'],
      };
    case '5':
      return {
        color: theme.color.custom01['90'],
        backgroundColor: theme.color.custom01['50'],
      };
    case '6':
      return {
        color: theme.color.custom16['90'],
        backgroundColor: theme.color.custom16['50'],
      };
    case '7':
      return {
        color: theme.color.custom15['90'],
        backgroundColor: theme.color.custom15['50'],
      };
    case '8':
      return {
        color: theme.color.custom13['90'],
        backgroundColor: theme.color.custom13['50'],
      };
    case '9':
      return {
        color: theme.color.custom11['90'],
        backgroundColor: theme.color.custom11['50'],
      };
    /**========================================================================
     **                            CHARS
     *========================================================================**/
    case 'a':
      return {
        color: theme.color.custom01['50'],
        backgroundColor: theme.color.custom01['90'],
      };
    case 'b':
      return {
        color: theme.color.custom02['50'],
        backgroundColor: theme.color.custom02['90'],
      };
    case 'c':
      return {
        color: theme.color.custom03['50'],
        backgroundColor: theme.color.custom03['90'],
      };
    case 'd':
      return {
        color: theme.color.custom04['90'],
        backgroundColor: theme.color.custom04['50'],
      };
    case 'e':
      return {
        color: theme.color.custom05['50'],
        backgroundColor: theme.color.custom05['90'],
      };
    case 'f':
      return {
        color: theme.color.custom06['90'],
        backgroundColor: theme.color.custom06['50'],
      };
    case 'g':
      return {
        color: theme.color.custom07['90'],
        backgroundColor: theme.color.custom07['50'],
      };
    case 'h':
      return {
        color: theme.color.custom08['50'],
        backgroundColor: theme.color.custom08['90'],
      };
    case 'i':
      return {
        color: theme.color.custom09['50'],
        backgroundColor: theme.color.custom09['90'],
      };
    case 'j':
      return {
        color: theme.color.custom10['50'],
        backgroundColor: theme.color.custom10['90'],
      };
    case 'k':
      return {
        color: theme.color.custom11['90'],
        backgroundColor: theme.color.custom11['50'],
      };
    case 'l':
      return {
        color: theme.color.custom12['50'],
        backgroundColor: theme.color.custom12['90'],
      };
    case 'm':
      return {
        color: theme.color.custom13['50'],
        backgroundColor: theme.color.custom13['90'],
      };
    case 'n':
      return {
        color: theme.color.custom14['50'],
        backgroundColor: theme.color.custom14['90'],
      };
    case 'o':
      return {
        color: theme.color.custom15['50'],
        backgroundColor: theme.color.custom15['90'],
      };
    case 'p':
      return {
        color: theme.color.custom16['50'],
        backgroundColor: theme.color.custom16['90'],
      };
    case 'q':
      return {
        color: theme.color.custom12['90'],
        backgroundColor: theme.color.custom12['50'],
      };
    case 'r':
      return {
        color: theme.color.custom17['50'],
        backgroundColor: theme.color.custom17['90'],
      };
    case 's':
      return {
        color: theme.color.custom10['90'],
        backgroundColor: theme.color.custom10['50'],
      };
    case 't':
      return {
        color: theme.color.custom01['90'],
        backgroundColor: theme.color.custom01['50'],
      };
    case 'u':
      return {
        color: theme.color.custom02['90'],
        backgroundColor: theme.color.custom02['50'],
      };
    case 'v':
      return {
        color: theme.color.custom03['90'],
        backgroundColor: theme.color.custom03['50'],
      };
    case 'w':
      return {
        color: theme.color.custom18['90'],
        backgroundColor: theme.color.custom18['50'],
      };
    case 'x':
      return {
        color: theme.color.custom05['90'],
        backgroundColor: theme.color.custom05['50'],
      };
    case 'y':
      return {
        color: theme.color.custom08['90'],
        backgroundColor: theme.color.custom08['50'],
      };
    case 'z':
      return {
        color: theme.color.custom15['90'],
        backgroundColor: theme.color.custom15['50'],
      };
    default:
      return {
        color: theme.color.tint.onPrimary,
        backgroundColor: theme.color.surface.secondary,
      };
  }
}

export function useAvatarColors(startLetter: string) {
  const theme = useTheme();

  const colors = React.useMemo(
    () => getColorCombinations(startLetter, theme),
    [theme, startLetter],
  );

  return { colors };
}

/**
 * Create initials from name
 * @param name name of user
 * @returns initials
 */
export function createInitialsFromName(name: string) {
  const names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }

  return initials;
}

/**
 * Create initials from profile
 * @param name name of user
 * @returns initials
 */
export function createInitialsFromProfile(profile: { firstName: string; lastName: string }) {
  return `${profile.firstName[0].toUpperCase()}${profile.lastName[0].toUpperCase()}`;
}

const rgx = new RegExp(/\b[a-zA-Z0-9]/, 'g');

/**
 * Create initials from profile
 * @param name name of user
 * @returns initials
 */
export function createInitialsFromString(name: string) {
  const matches = Array.from(name.matchAll(rgx));
  return matches[0][0] ?? '-';
}
